/**
 * Location and filtering utilities
 */
import { CITY_TO_METRO, METRO_COORDS } from "../constants/networkCities";

// Reverse-build metro → [city names lowercased] from CITY_TO_METRO so we can
// expand a search like "Miami" into the full South-Florida list (Brickell,
// Coral Gables, Doral, etc.) the user actually expects.
const METRO_TO_CITIES = (() => {
  const map = {};
  for (const [city, metro] of Object.entries(CITY_TO_METRO)) {
    if (!map[metro]) map[metro] = [];
    map[metro].push(city.toLowerCase());
  }
  return map;
})();

const NORMALIZED_CITY_TO_METRO = Object.fromEntries(
  Object.entries(CITY_TO_METRO).map(([city, metro]) => [city.toLowerCase(), metro])
);

/**
 * Expand a user-typed city query to every city in its metro (lowercased).
 * Returns an empty array if no query is given; for queries not in any known
 * metro, returns the single normalized query so callers can still match it.
 *
 * @param {string} query - Raw search input (e.g. "Miami", "miami, fl")
 * @returns {string[]} - Lowercased city names in the same metro
 */
export const expandQueryToMetroCities = (query) => {
  if (!query || !query.trim()) return [];
  const normalized = query.trim().toLowerCase();
  const extracted = normalized.split(",")[0].trim();
  const metro =
    NORMALIZED_CITY_TO_METRO[normalized] || NORMALIZED_CITY_TO_METRO[extracted];
  if (metro && METRO_TO_CITIES[metro]) return METRO_TO_CITIES[metro];
  return [extracted || normalized];
};

// Haversine distance between two lat/lng pairs, in miles.
const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
};

/**
 * Find the nearest known RunCards metro within `maxMiles` of a coordinate.
 * Returns { name, dist } or null when nothing is in range.
 */
export const findNearestMetro = (lat, lng, maxMiles = 75) => {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  let best = null;
  for (const [name, c] of Object.entries(METRO_COORDS)) {
    if (typeof c.lat !== "number" || typeof c.lng !== "number") continue;
    const d = haversineMiles(lat, lng, c.lat, c.lng);
    if (d <= maxMiles && (!best || d < best.dist)) best = { name, dist: d };
  }
  return best;
};

/**
 * Expand a coordinate to every city in its metro (lowercased). Empty array
 * when the user isn't inside any known metro's radius — callers should fall
 * back to plain distance-sorted results in that case.
 */
export const expandLatLngToMetroCities = (lat, lng, maxMiles = 75) => {
  const metro = findNearestMetro(lat, lng, maxMiles);
  if (!metro) return [];
  return METRO_TO_CITIES[metro.name] || [];
};

/** US state / DC postal-style abbreviations only (not full names, not regions like "Occitanie"). */
const US_STATE_ABBREVIATIONS = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV',
  'WI', 'WY', 'DC',
]);

function isUsStateAbbreviation(state) {
  if (!state || typeof state !== 'string') return false;
  const s = state.trim().toUpperCase();
  return s.length === 2 && US_STATE_ABBREVIATIONS.has(s);
}

function regionDisplayName(iso2) {
  if (!iso2 || typeof iso2 !== 'string') return '';
  const code = iso2.trim().toUpperCase();
  if (!code) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

/**
 * Runner profile location for runcards and previews: US shows city + state; non-US shows city + country from ISO code.
 * Never appends `state` as a second token outside the US (avoids showing regions like "Occitanie" as "country").
 * Parity with mobile `formatHeaderCityState`.
 * @param {{ city?: string, state?: string, country?: string }|null|undefined} user
 * @returns {string}
 */
export function formatProfileLocationLabel(user) {
  if (!user?.city) return '';
  const country = typeof user.country === 'string' ? user.country.trim().toUpperCase() : '';
  if (country === 'US') {
    return user.state ? `${user.city}, ${user.state}` : user.city;
  }
  if (country && country !== 'US') {
    const label = regionDisplayName(country);
    return label ? `${user.city}, ${label}` : user.city;
  }
  if (user.state && isUsStateAbbreviation(user.state)) {
    return `${user.city}, ${user.state.trim().toUpperCase()}`;
  }
  return user.city;
}

/**
 * Get location object for API calls (user coords or guest coords).
 * Same logic as mobile RunScreen: prefer logged-in user's stored location, then guest browser location.
 * @param {Object|null} user - Current user (with latitude, longitude) or null
 * @param {Object|string|null} guestLocation - Guest location { lat, lon } or "denied"/"skipped"/null
 * @returns {{ latitude: number, longitude: number }|null}
 */
export const getLocationForApi = (user, guestLocation) => {
  if (user && user.latitude != null && user.longitude != null) {
    return { latitude: user.latitude, longitude: user.longitude };
  }
  if (guestLocation && typeof guestLocation === "object" && guestLocation.lat != null && guestLocation.lon != null) {
    return { latitude: guestLocation.lat, longitude: guestLocation.lon };
  }
  return null;
};

/**
 * Build query params for GET /clubs (limit, user_lat, user_lon, user_city).
 * @param {Object} opts
 * @param {number} opts.limit
 * @param {{ latitude: number, longitude: number }|null} opts.location - From getLocationForApi
 * @param {string} [opts.userCity]
 * @returns {Object}
 */
export const getClubsListParams = ({ limit, location, userCity }) => {
  const p = { limit };
  if (location) {
    p.user_lat = location.latitude;
    p.user_lon = location.longitude;
  }
  if (typeof userCity === "string" && userCity.trim()) p.user_city = userCity.trim();
  return p;
};

/**
 * Extract city name from location string (handles "City, State" format)
 * @param {string} location - Location string (e.g., "Miami, FL")
 * @returns {string} - City name (e.g., "Miami")
 */
export const extractCityName = (location) => {
  if (!location) return '';
  return location.toLowerCase().split(',')[0].trim();
};

/**
 * Check if a club matches a search term (searches name, city, and state)
 * @param {Object} club - Club object
 * @param {string} searchTerm - Search term
 * @returns {boolean} - Whether club matches search
 */
export const clubMatchesSearch = (club, searchTerm) => {
  if (!searchTerm || !club) return false;
  
  const term = searchTerm.toLowerCase();
  const cityName = extractCityName(searchTerm);
  
  return (
    club.name?.toLowerCase().includes(term) ||
    club.city?.toLowerCase().includes(cityName) ||
    club.state?.toLowerCase().includes(term)
  );
};

/**
 * Filter clubs by city (extracts city name from location string)
 * @param {Array} clubs - Array of club objects
 * @param {string} location - Location string (e.g., "Miami, FL")
 * @returns {Array} - Filtered clubs
 */
export const filterClubsByCity = (clubs, location) => {
  if (!location || !clubs) return clubs;

  const cityName = extractCityName(location);
  const normalizedCityName = cityName.toLowerCase().trim();

  // Expand to the full metro list so e.g. "Miami" matches Brickell, Coral
  // Gables, Doral, etc. For non-metro queries this returns just the query.
  const metroCities = expandQueryToMetroCities(location);
  const metroSet = new Set(metroCities);

  return clubs.filter(club => {
    if (!club.city) return false;

    const clubCity = club.city.toLowerCase().trim();

    // Metro-aware exact match — covers all sibling cities in the same metro.
    if (metroSet.has(clubCity)) return true;

    // Legacy substring fallbacks for cities not in the metro map.
    if (clubCity === normalizedCityName) return true;
    if (clubCity.includes(normalizedCityName)) return true;
    if (normalizedCityName.includes(clubCity)) return true;
    const clubCityBase = clubCity.split(' ')[0];
    const searchCityBase = normalizedCityName.split(' ')[0];
    if (clubCityBase === searchCityBase && clubCityBase.length > 2) return true;

    return false;
  });
};

/**
 * Filter clubs by search query (searches name, city, and state)
 * @param {Array} clubs - Array of club objects
 * @param {string} searchQuery - Search query
 * @returns {Array} - Filtered clubs
 */
export const filterClubsBySearch = (clubs, searchQuery) => {
  if (!searchQuery || !clubs) return clubs;
  
  return clubs.filter(club => clubMatchesSearch(club, searchQuery));
};

/**
 * Filter clubs by type
 * @param {Array} clubs - Array of club objects
 * @param {string} clubType - Club type to filter by
 * @returns {Array} - Filtered clubs
 */
export const filterClubsByType = (clubs, clubType) => {
  if (!clubType || clubType === 'All' || !clubs) return clubs;
  
  return clubs.filter(club => club.type === clubType);
};

/**
 * Extract state from location string (handles "City, State" format)
 * @param {string} location - Location string (e.g., "Miami, FL")
 * @returns {string} - State abbreviation or empty string
 */
const extractState = (location) => {
  if (!location) return '';
  const parts = location.split(',');
  return parts.length > 1 ? parts[1].trim() : '';
};

/**
 * Filter clubs by state
 * @param {Array} clubs - Array of club objects
 * @param {string} state - State abbreviation
 * @returns {Array} - Filtered clubs
 */
const filterClubsByState = (clubs, state) => {
  if (!state || !clubs) return [];
  const normalizedState = state.toLowerCase();
  return clubs.filter(club => 
    club.state?.toLowerCase() === normalizedState
  );
};

/**
 * Get featured clubs with fallback logic
 * @param {Array} clubs - Array of club objects
 * @param {string} searchCity - Search city
 * @param {string} userLocation - User's location
 * @param {number} limit - Maximum number of clubs to return
 * @returns {Array} - Featured clubs
 */
export const getFeaturedClubs = (clubs, searchCity, userLocation, limit = 3) => {
  if (!clubs || clubs.length === 0) return [];
  
  // If user has typed something in search bar, use that
  if (searchCity?.trim()) {
    const filtered = filterClubsByCity(clubs, searchCity);
    // If no matches, try to find clubs in the same state
    if (filtered.length === 0 && userLocation) {
      const state = extractState(userLocation);
      if (state) {
        const stateFiltered = filterClubsByState(clubs, state);
        if (stateFiltered.length > 0) {
          return stateFiltered.slice(0, limit);
        }
      }
    }
    // Return filtered results (even if empty) when user has searched
    return filtered.slice(0, limit);
  }
  
  // If no search input but user has location, use that
  if (userLocation?.trim()) {
    const filtered = filterClubsByCity(clubs, userLocation);
    // If no matches, try to find clubs in the same state
    if (filtered.length === 0) {
      const state = extractState(userLocation);
      if (state) {
        const stateFiltered = filterClubsByState(clubs, state);
        if (stateFiltered.length > 0) {
          return stateFiltered.slice(0, limit);
        }
      }
    }
    // Return filtered results (even if empty) when user has location
    return filtered.slice(0, limit);
  }
  
  // No search input and no location - show first few clubs as fallback
  // This ensures clubs always appear on the dashboard
  return clubs.slice(0, limit);
};
